
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { customerReviewsApi } from "@/lib/api";
import toast from "react-hot-toast";

interface Review {
  id: number;
  customer_name: string;
  location: string;
  rating: number;
  review_text: string;
  product_name: string | null;
  image_url: string | null;
  is_featured: boolean;
  display_order: number;
  is_active: boolean;
}

export default function CustomerReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await customerReviewsApi.getFeatured(6);
        setReviews(response.reviews || []);
      } catch (error) {
        console.error("Error fetching reviews:", error);
        toast.error("Failed to load reviews");
        // Fallback to empty array
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  if (loading) {
    return (
      <section id="reviews" className="py-12 bg-gradient-to-br from-gray-50 via-white to-aarsa-cream-100 mt-4 mb-4 scroll-mt-24">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-yellow-500 -yellow-500" />
              <h2 className="text-3xl md:text-4xl font-bold font-playfair">
                What Our <span className="gradient-text">Customers Say</span>
              </h2>
              <Star className="w-5 h-5 text-yellow-500 -yellow-500" />
            </div>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Real reviews from thousands of satisfied customers across India
            </p>
          </motion.div>

          {/* Skeleton Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative"
              >
                <div className="bg-white rounded-xl p-5 shadow-sm h-full flex flex-col border border-gray-100">
                  {/* Quote Icon Skeleton */}
                  <div className="absolute top-4 right-4 opacity-10">
                    <Quote className="w-12 h-12 text-gray-300" />
                  </div>

                  {/* Stars Skeleton */}
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <div
                        key={j}
                        className="w-4 h-4 bg-gray-200 rounded animate-pulse"
                      />
                    ))}
                  </div>

                  {/* Review Text Skeleton */}
                  <div className="space-y-2 mb-4 flex-grow">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                  </div>

                  {/* Customer Info Skeleton */}
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                    <div className="flex-grow min-w-0 space-y-2">
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust Badge Skeleton */}
          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow-md">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
              <div className="h-5 w-px bg-gray-300" />
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-5 w-px bg-gray-300" />
              <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return null;
  }
  return (
    <section id="reviews" className="py-12 bg-gradient-to-br from-gray-50 via-white to-aarsa-cream-100 mt-4 mb-4 scroll-mt-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-yellow-500 -yellow-500" />
            <h2 className="text-3xl md:text-4xl font-bold font-playfair">
              What Our <span className="gradient-text">Customers Say</span>
            </h2>
            <Star className="w-5 h-5 text-yellow-500 -yellow-500" />
          </div>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Real reviews from thousands of satisfied customers across India
          </p>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100">
                {/* Quote Icon */}
                <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Quote className="w-12 h-12 text-aarsa-gold-600" />
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < review.rating
                          ? "text-yellow-500 fill-yellow-500"
                          : "text-gray-300 fill-gray-300"
                      }`}
                    />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-700 mb-4 flex-grow leading-relaxed relative z-10 text-sm">
                  "{review.review_text}"
                </p>

                {/* Customer Info */}
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-aarsa-gold-400 to-aarsa-gold-500 flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                    {review.customer_name.charAt(0)}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate text-sm">
                      {review.customer_name}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {review.location}
                    </p>
                    {review.product_name && (
                      <p className="text-xs text-aarsa-gold-600 mt-0.5 truncate">
                        {review.product_name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Decorative Gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-aarsa-gold-500 via-aarsa-gold-600 to-aarsa-sage-500 rounded-b-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center mt-8"
        >
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-white rounded-full shadow-md">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < 5
                      ? "text-yellow-500 fill-yellow-500"
                      : "text-gray-300 fill-gray-300"
                  }`}
                />
              ))}
            </div>
            <div className="h-5 w-px bg-gray-300" />
            <span className="text-gray-700 font-semibold text-sm">
              <span className="text-aarsa-gold-600 font-bold">4.9</span> Average Rating
            </span>
            <div className="h-5 w-px bg-gray-300" />
            <span className="text-gray-700 font-semibold text-sm">
              <span className="text-aarsa-gold-600 font-bold">22K+</span> Happy Customers
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
